import { MouseEvent } from 'react';
import { Message } from '@chatscope/chat-ui-kit-react';
import { format } from 'date-fns';

import style from './SupportWidget.module.scss';

import type { SupportAttachment, SupportMessage, SupportPerson } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { translate } from '@chaika/i18n/helper';

import { ReceivedSupportAttachment } from './SupportAttachmentPreview';

interface TicketMessageProps {
    message: SupportMessage;
    participants: SupportPerson[];
    onOpenAttachment(event: MouseEvent<HTMLAnchorElement>, attachment: SupportAttachment): void;
    onUrlExpired(): void;
}

export function TicketMessage({ message, participants, onOpenAttachment, onUrlExpired }: TicketMessageProps) {
    const isOwnMessage = isWrittenByCurrentDoctor(message, participants);
    const hasAttachments = message.attachments.length > 0;
    const sentTime = format(new Date(message.createdAt), 'HH:mm');

    return (
        <Message
            data-message-id={message.id}
            model={{
                direction: isOwnMessage ? 'outgoing' : 'incoming',
                message: hasAttachments ? '' : message.text,
                position: 'single',
                sender: message.authorName,
                sentTime,
                type: 'text',
            }}
        >
            {hasAttachments && (
                <Message.CustomContent>
                    {message.text && <div className={style.messageAttachmentText}>{message.text}</div>}
                    <div className={style.messageAttachments}>
                        {message.attachments.map(attachment => (
                            <ReceivedSupportAttachment
                                key={attachment.id}
                                attachment={attachment}
                                onOpen={onOpenAttachment}
                                onUrlExpired={onUrlExpired}
                            />
                        ))}
                    </div>
                </Message.CustomContent>
            )}
            <Message.Header
                sender={message.authorName}
                sentTime={sentTime}
            />
            {message.editedAt && (
                <Message.Footer>
                    <span className={style.editedMarker}>{translate('supportEdited')}</span>
                </Message.Footer>
            )}
        </Message>
    );
}

function isWrittenByCurrentDoctor(message: SupportMessage, participants: SupportPerson[]): boolean {
    if (message.author !== 'doctor') return false;
    if (message.authorId) return message.authorId === store.authUser.id;

    const currentDoctor = participants.find(participant => participant.id === store.authUser.id);
    return !currentDoctor || message.authorName === currentDoctor.name;
}
